import React, { useState, useEffect, useRef } from "react";
import Layout from "../../components/Layout";
import { ChevronLeft, FilePlus2, Loader2, X, Upload } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  PostsService,
  CreatePostRequest,
  UpdatePostRequest,
} from "../../services/postsService";
import { CategoriesService } from "../../services/categoriesService";
import "./../../styles/member/createarticle.css";
import { showToast } from "../../utils/toastUtils";
import defaultThumbnail from "../../assets/images/default-thumbnail.webp";
import CKEditorWrapper from "../../components/CKEditorWrapper";
import { toAbsoluteUrl } from "../../config/api";

const EditArticle: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  
  // Constants for validation
  const CONTENT_MIN_LENGTH = 100;
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    metaDescription: "",
    primaryKeyword: "",
    secondaryKeywords: "",
    anchorText: "",
    featuredImage: "",
    category: "",
    categoryId: undefined as number | undefined,
    tags: [] as string[],
    internalLinks: [] as string[],
    externalLinks: [] as string[],
  });
  const [originalStatus, setOriginalStatus] = useState<string | null>(null);
  const [moderationWarning, setModerationWarning] = useState<{
    show: boolean;
    message: string;
    flaggedTerms: string[];
  }>({ show: false, message: "", flaggedTerms: [] });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tải danh mục và dữ liệu bài viết khi component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Tải danh mục
        const categoriesResponse = await CategoriesService.listCategories({
          limit: 100,
        });
        setCategories(categoriesResponse.data);

        // Tải dữ liệu bài viết nếu ID được cung cấp
        if (id) {
          setLoading(true);
          const article = await PostsService.getPostById(parseInt(id));

          // Chuyển đổi mảng từ khoá phụ thành chuỗi
          const secondaryKeywordsString =
            article.secondaryKeywords?.join(", ") || "";

          setFormData({
            title: article.title || "",
            content: article.content || "",
            metaDescription: article.metaDescription || "",
            primaryKeyword: article.primaryKeyword || "",
            secondaryKeywords: secondaryKeywordsString,
            anchorText: article.anchorText || "",
            featuredImage: article.featuredImage || "",
            category: article.category?.name || "",
            categoryId: article.categoryId,
            tags: article.tags || [],
            internalLinks: article.internalLinks || [],
            externalLinks: article.externalLinks || [],
          });
          setOriginalStatus(article.status);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Có lỗi xảy ra khi tải dữ liệu"
        );
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast.error('Chỉ chấp nhận file ảnh (JPG, JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Kích thước file không được vượt quá 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await PostsService.uploadImage(file);
      handleInputChange("featuredImage", result.url);
      showToast.success(result.message || 'Upload hình ảnh thành công');
    } catch (error: any) {
      showToast.error(error.message || 'Upload hình ảnh thất bại');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Kiểm tra các trường bắt buộc
      if (!formData.title.trim()) {
        throw new Error("Tiêu đề bài viết không được để trống");
      }
      if (!formData.content.trim()) {
        throw new Error("Nội dung bài viết không được để trống");
      }
      if (!formData.category && !formData.categoryId) {
        throw new Error("Vui lòng chọn danh mục");
      }
      // if (!formData.featuredImage.trim()) {
      //   throw new Error("URL hình ảnh nổi bật không được để trống");
      // }

      // Chuyển đổi chuỗi từ khoá phụ thành mảng
      const secondaryKeywords = formData.secondaryKeywords
        ? formData.secondaryKeywords
            .split(",")
            .map((k: string) => k.trim())
            .filter((k: string) => k)
        : [];

      if (id) {
        // Kiểm duyệt trước khi cập nhật (lần 1)
        // Extract image URLs from content + featuredImage
        const imageUrls: string[] = [];
        try {
          const urlRegex = /(https?:\/\/[^\s"']+\.(?:png|jpg|jpeg|webp|gif))/gi;
          let m: RegExpExecArray | null;
          while ((m = urlRegex.exec(formData.content || "")) !== null) {
            imageUrls.push(m[1]);
          }
          if (formData.featuredImage) imageUrls.push(formData.featuredImage);
        } catch {}

        const moderation = await PostsService.moderateContent({
          title: formData.title,
          content: formData.content,
          imageUrls,
        });
        if (!moderation.isSafe) {
          // Ưu tiên hiển thị cảnh báo trước, ngay cả khi có từ ngữ bị chặn
          if (moderation.severity === "warn") {
            setModerationWarning({
              show: true,
              message: moderation.message || "Nội dung có từ ngữ nhạy cảm.",
              flaggedTerms: moderation.flaggedTerms,
            });
            setSubmitting(false);
            return; // chờ người dùng xác nhận
          }
          // Chỉ chặn hoàn toàn khi chỉ có từ ngữ bị chặn mà không có cảnh báo
          if (moderation.severity === "block") {
            throw new Error(
              moderation.message || "Nội dung không đạt kiểm duyệt"
            );
          }
        }

        // Cập nhật bài viết hiện có
        const updateData: UpdatePostRequest = {
          ...formData,
          secondaryKeywords,
          // Chỉ gửi categoryId, không gửi category để tránh conflict
          categoryId: formData.categoryId || undefined,
        };

        const updatedPost = await PostsService.updatePost(
          parseInt(id),
          updateData
        );

        // Hiển thị thông báo nếu bài viết được chuyển về pending
        if (updatedPost.status === "pending") {
          showToast.warning(
            "Bài viết đã được cập nhật và chuyển về trạng thái chờ duyệt. Vui lòng chờ đợi trong khi chúng tôi xem xét lại nội dung."
          );
        }

        // Thêm thông báo thành công
        showToast.success("Bài viết đã được cập nhật thành công!");

        // Force reload trang article để đảm bảo dữ liệu mới được load
        navigate("/member/article", { replace: true });
        window.location.reload();
      } else {
        // Tạo bài viết mới
        const submitData: CreatePostRequest = {
          ...formData,
          secondaryKeywords,
          category: formData.category || undefined,
          categoryId: formData.categoryId || undefined,
        };

        await PostsService.createPost(submitData);
        navigate("/member/article");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Có lỗi xảy ra khi tạo bài viết"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmOverride = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const secondaryKeywords = formData.secondaryKeywords
        ? formData.secondaryKeywords
            .split(",")
            .map((k: string) => k.trim())
            .filter((k: string) => k)
        : [];
      const externalLinks =
        formData.externalLinks
          .filter((link) => link.trim())
          .filter((link) => /^https?:\/\//.test(link.trim())) || [];
      await PostsService.updatePost(parseInt(id!), {
        ...formData,
        secondaryKeywords,
        externalLinks,
        categoryId: formData.categoryId || undefined,
        confirmOverride: true,
      });
      showToast.warning(
        "Bài viết đã được cập nhật (bỏ qua cảnh báo). Tài khoản có thể bị khóa theo chính sách."
      );
      navigate("/member/article");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        (err instanceof Error
          ? err.message
          : "Có lỗi xảy ra khi cập nhật bài viết");
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      setModerationWarning({ show: false, message: "", flaggedTerms: [] });
    }
  };

  return (
    <Layout>
      <div className="article-page">
        {/* Tiêu đề */}
        <Link to="/member/article" className="btn-back">
          <ChevronLeft size={16} /> Quay lại
        </Link>
        <div className="article-header-infor">
          <div>
            <h2>{id ? "Sửa bài viết" : "Tạo bài viết"}</h2>
            <p>
              {id
                ? "Chỉnh sửa và cập nhật bài viết"
                : "Viết và tối ưu hoá bài viết để đăng tải"}
            </p>
          </div>
        </div>

        {/* Overlay loading khi đang tải dữ liệu bài viết */}
        {loading && id && (
          <div className="overlay-article">
            <div className="overlay-article-loading">
              <Loader2 size={24} className="overlay-article-icon" />
              <span>Đang tải dữ liệu bài viết...</span>
            </div>
          </div>
        )}

        {/* Thông báo lỗi */}
        {error && <div className="error-message">{error}</div>}

        {/* Modal cảnh báo kiểm duyệt */}
        {moderationWarning.show && (
          <div
            className="modal-overlay"
            onClick={() =>
              setModerationWarning({
                show: false,
                message: "",
                flaggedTerms: [],
              })
            }
          >
            <div
              className="modal-content-cencor"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header-cencor">
                <h3>Cảnh báo kiểm duyệt</h3>
                <button
                  onClick={() =>
                    setModerationWarning({
                      show: false,
                      message: "",
                      flaggedTerms: [],
                    })
                  }
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ color: "#874d00", marginBottom: 4 }}>
                  {moderationWarning.message}
                </div>
                {moderationWarning.flaggedTerms?.length > 0 && (
                  <div className="moderation-warning-container">
                    <div className="moderation-warning-title">
                      Từ ngữ vi phạm:
                    </div>
                    <div className="moderation-terms-container">
                      {moderationWarning.flaggedTerms
                        .sort((a: string, b: string) => {
                          // Ưu tiên hiển thị từ ngữ "warn" trước "block"
                          // Từ ngữ "warn" thường không có prefix, "block" có thể có prefix "image:"
                          const aIsWarn = !a.startsWith("image:");
                          const bIsWarn = !b.startsWith("image:");
                          if (aIsWarn && !bIsWarn) return -1;
                          if (!aIsWarn && bIsWarn) return 1;
                          return 0;
                        })
                        .map((term: string, index: number) => (
                          <span
                            key={index}
                            className={`moderation-term-badge ${
                              !term.startsWith("image:")
                                ? "moderation-term-badge--warn"
                                : "moderation-term-badge--block"
                            }`}
                          >
                            {term}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() =>
                      setModerationWarning({
                        show: false,
                        message: "",
                        flaggedTerms: [],
                      })
                    }
                  >
                    Tôi sẽ chỉnh sửa
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleConfirmOverride}
                  >
                    Vẫn cập nhật (có thể bị khóa)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thông báo cảnh báo cho bài viết đã duyệt */}
        {originalStatus === "approved" && (
          <div className="warning-message">
            <strong>⚠️ Lưu ý:</strong> Bài viết này đã được duyệt. Nếu bạn thực
            hiện thay đổi, bài viết sẽ được chuyển về trạng thái "Chờ duyệt" và
            cần được xem xét lại.
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-article">
          {/* TRÁI */}
          <div className="left-panel">
            {/* Nội dung bài viết */}
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Nội dung bài viết</h3>
                <span className="article-content_subtitle">
                  Thông tin chính của bài viết
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  Tiêu đề bài viết *
                </label>
                <input
                  className="article-content_input"
                  type="text"
                  placeholder="Nhập tiêu đề hấp dẫn cho bài viết"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  required
                />
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  Nội dung bài viết *
                </label>
                <CKEditorWrapper
                  value={formData.content}
                  onChange={(content) => handleInputChange("content", content)}
                  placeholder="Viết nội dung bài viết chất lượng, ít nhất 100 ký tự......"
                  minLength={CONTENT_MIN_LENGTH}
                />
              </div>
            </div>

            {/* Tối ưu SEO */}
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Tối ưu SEO</h3>
                <span className="article-content_subtitle">
                  Thông tin SEO để bài viết lên top Google
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">Mô tả Meta</label>
                <textarea
                  className="article-content_textarea"
                  placeholder="Mô tả SEO tối đa 160 ký tự..."
                  rows={3}
                  value={formData.metaDescription}
                  onChange={(e) =>
                    handleInputChange("metaDescription", e.target.value)
                  }
                />
                <span className="article-content_hint">
                  {formData.metaDescription?.length || 0}/160 ký tự
                </span>
              </div>
              <div className="article-content_seo-grid">
                <div className="article-content_input-group">
                  <label className="article-content_label">Từ khoá chính</label>
                  <input
                    className="article-content_input"
                    type="text"
                    placeholder="Từ khoá chính..."
                    value={formData.primaryKeyword}
                    onChange={(e) =>
                      handleInputChange("primaryKeyword", e.target.value)
                    }
                  />
                </div>
                <div className="article-content_input-group">
                  <label className="article-content_label">Từ khoá phụ</label>
                  <input
                    className="article-content_input"
                    type="text"
                    placeholder="Từ khoá phụ, cách nhau bằng dấu phẩy..."
                    value={formData.secondaryKeywords}
                    onChange={(e) =>
                      handleInputChange("secondaryKeywords", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">
                  URL hình ảnh nổi bật
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    className="article-content_input"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={formData.featuredImage}
                    onChange={(e) =>
                      handleInputChange("featuredImage", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={uploadingImage}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Đang tải...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Upload
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Ảnh xem trước */}
                <div className="article-image-preview">
                  <img
                    src={
                      formData.featuredImage
                        ? (formData.featuredImage.startsWith('http') 
                            ? formData.featuredImage 
                            : toAbsoluteUrl(formData.featuredImage))
                        : defaultThumbnail
                    }
                    alt="Ảnh xem trước"
                    className="article-image-preview-img"
                    onError={(e) => (e.currentTarget.src = defaultThumbnail)}
                  />
                </div>
              </div>
              {/* <div className="article-content_input-group">
                <label className="article-content_label">Anchor text</label>
                <input
                  className="article-content_input"
                  type="text"
                  placeholder="Văn bản hiển thị cho liên kết"
                  value={formData.anchorText}
                  onChange={(e) =>
                    handleInputChange("anchorText", e.target.value)
                  }
                />
              </div> */}
            </div>
          </div>

          {/* PHẢI */}
          <div className="right-panel">
            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Thông tin bài viết</h3>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">Danh mục *</label>
                <select
                  className="article-content_select"
                  value={formData.categoryId || ""}
                  onChange={(e) => {
                    const categoryId = e.target.value
                      ? parseInt(e.target.value)
                      : undefined;
                    const category = categories.find(
                      (c) => c.id === categoryId
                    );
                    handleInputChange("categoryId", categoryId);
                    handleInputChange("category", category?.name || "");
                  }}
                  required
                >
                  <option value="">Chọn danh mục</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="article-content">
              <div className="article-content_header">
                <h3 className="article-content_title">Thông tin Backlink</h3>
                <span className="article-content_subtitle">
                  Thông tin về liên kết bạn muốn xây dựng
                </span>
              </div>
              <div className="article-content_input-group">
                <label className="article-content_label">URL Backlink</label>
                <input
                  className="article-content_input"
                  type="url"
                  placeholder="http://example.com"
                  value={formData.externalLinks?.[0] || ""}
                  onChange={(e) =>
                    handleInputChange("externalLinks", [e.target.value])
                  }
                />
              </div>
              {/* <div className="article-content_input-group">
                <label className="article-content_label">Anchor text</label>
                <input
                  className="article-content_input"
                  type="text"
                  placeholder="Văn bản hiển thị cho liên kết"
                  value={formData.anchorText}
                  onChange={(e) =>
                    handleInputChange("anchorText", e.target.value)
                  }
                />
              </div> */}
            </div>

            <button
              type="submit"
              className={`create-acticle-btn ${
                submitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {id ? "Đang cập nhật..." : "Đang tạo..."}
                </>
              ) : (
                <>
                  <FilePlus2 size={16} />
                  {id ? "Cập nhật bài viết" : "Tạo bài viết"}
                </>
              )}
            </button>
            {/* <button
              type="button"
              className={`create-acticle-btn ${
                submitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={submitting}
              onClick={() => {
                if (!submitting) {
                  // Chức năng xem trước - sẽ được phát triển sau
                  showToast.info("Chức năng xem trước sẽ được phát triển");
                }
              }}
            >
              <Eye size={16} /> Xem trước
            </button> */}
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditArticle;
